/**
 * Lightweight UUID v4 generator and deterministic UUID generator
 * for reproducible seeding across platforms.
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Deterministic pseudo-UUID from a string seed (e.g. "COMBINED_MATHS_2024_PAPER_I")
 * Uses FNV-1a 32-bit hash extended to 128 bits.
 */
export function deterministicUUID(seed: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x811c9dc5 ^ 0x55555555;
  let h3 = 0x811c9dc5 ^ 0xaaaaaaaa;
  let h4 = 0x811c9dc5 ^ 0x33333333;

  for (let i = 0; i < seed.length; i++) {
    const c = seed.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ (c << 1), 0x01000193);
    h3 = Math.imul(h3 ^ (c << 2), 0x01000193);
    h4 = Math.imul(h4 ^ (c << 3), 0x01000193);
  }

  const p1 = (h1 >>> 0).toString(16).padStart(8, '0');
  const p2 = ((h2 >>> 16) & 0xffff).toString(16).padStart(4, '0');
  const p3 = '5' + ((h2 >>> 4) & 0x0fff).toString(16).padStart(3, '0'); // v5 format
  const p4 = ((0x80 | ((h3 >>> 24) & 0x3f)) << 8 | ((h3 >>> 16) & 0xff)).toString(16).padStart(4, '0');
  const p5 = ((h3 & 0xffff) >>> 0).toString(16).padStart(4, '0') + (h4 >>> 0).toString(16).padStart(8, '0');

  return `${p1}-${p2}-${p3}-${p4}-${p5}`;
}
